import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  useRef,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withStyles } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import { selectLocale } from 'models/localization';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector, batch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { DEFAULT_THEME_COLOR, APP } from 'appenv';
import { SearchResultsSlice } from 'models/searchResults';
import { UserSessionContext } from 'components/UserSession';
import { SearchFieldContext } from 'components/SearchFieldContext';
import { TabsContext, TabsContextStateProvider } from 'components/TabsContext';
import BackToTopButton from 'components/BackToTopButton';
import FooterV2 from 'components/footer_v2';
import FloatingFooter from 'components/footer_v2/FloatingFooter';
import SearchFieldContainer from './SearchFieldContainer';
import SearchResultTabContentContainer from './SearchResultTabContentContainer';

const StyledDialog = withStyles(() => ({
  paper: {
    backgroundColor: '#F0F1F2',
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
  },
}))(Dialog);

const HeaderContainer = styled.div`
  flex: 0 0 auto;
  width: 100%;
`;

const StyledTabsContainer = ({ children, ...props }) => {
  const { themeColor } = useSelector((s) => s.eventConfigs);
  const CustomTabContainer = withStyles(() => ({
    root: {
      padding: '8px 3vw',
      minHeight: '52px',
    },
    indicator: {
      backgroundColor: themeColor || DEFAULT_THEME_COLOR,
      borderRadius: '100px',
      height: '100%',
      zIndex: 10,
    },
  }))(Tabs);
  return <CustomTabContainer {...props}>{children}</CustomTabContainer>;
};
StyledTabsContainer.propTypes = {
  children: PropTypes.node.isRequired,
};

const StyledTab = withStyles(() => ({
  root: {
    color: '#999',
    fontSize: '13px',
    fontWeight: 'bold',
    textTransform: 'none',
    flex: '1 0 auto',
    zIndex: 20,
    minHeight: 0,
    width: 'auto',
    letterSpacing: '0.09375px',
    lineHeight: '24px',
    whiteSpace: 'nowrap',
  },
  selected: {
    color: '#FFF',
  },
}))(Tab);

const BodyContainer = styled.div`
  flex: 1 1 auto;
  width: 100%;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
`;

const SearchTabs = ({ viewableTabs }) => {
  const { activeTabKey, setActiveTabKey } = useContext(TabsContext);

  const onTabClick = useCallback((_, selectedTab) => {
    setActiveTabKey(selectedTab);
  }, [setActiveTabKey]);

  return (
    <StyledTabsContainer
      value={activeTabKey}
      onChange={onTabClick}
      variant="scrollable"
      scrollButtons="auto"
    >
      {viewableTabs}
    </StyledTabsContainer>
  );
};

SearchTabs.propTypes = {
  viewableTabs: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

const SearchModal = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { location: { state } } = history;
  const [searchInputText, setSearchInputText] = useState((state && state.searchInputText) || '');

  const { canSee } = useContext(UserSessionContext);
  const { fetchSearchResultsWithDeboune } = useContext(SearchFieldContext);
  const {
    pageNames: {
      all,
      exhibitors,
      webinar,
      participant,
      products,
      speakers,
    },
    features,
  } = useSelector((s) => s.eventConfigs);
  const locale = useSelector(selectLocale);
  const showResult = searchInputText.length >= 2;
  const showTabs = APP !== 'ms624';
  const tabItems = useMemo(() => (
    [
      {
        label: all?.[locale] || t('all', 'All'),
        type: 'all',
        condition: true,
      },
      {
        label: exhibitors?.[locale] || t('page.exhibitors', 'Exhibitors'),
        type: 'exhibitors',
        condition: canSee('hall'),
      },
      {
        label: webinar?.[locale] || t('page.webinar', 'Webinars'),
        type: 'webinar',
        condition: canSee('webinar'),
      },
      {
        label: participant?.[locale] || t('page.participants', 'Participants'),
        type: 'participants',
        condition: canSee('participant'),
      },
      {
        label: products?.[locale] || t('page.products', 'Products'),
        type: 'products',
        condition: features.includes('productPage'),
      },
      {
        label: speakers?.[locale] || t('page.speakers', 'Speakers'),
        type: 'speakers',
        condition: canSee('webinar'),
      },
    ]
  ), [all, exhibitors, webinar, participant, products, speakers, canSee, locale, t, features]);
  const fetchMore = useCallback(() => {
    fetchSearchResultsWithDeboune({
      newSearchText: showResult ? searchInputText : '',
    });
  }, [fetchSearchResultsWithDeboune, searchInputText, showResult]);

  const isDataReady = useSelector((s) => !s.webinars.isLoading);

  const onSeacrhInputChange = (event) => {
    history.replace({
      search: new URL(window.location.href).search,
      state: {
        ...state,
        searchInputText: event.target.value,
      },
    });
  };

  const dispatch = useDispatch();

  useEffect(() => {
    const unlisten = history.listen(({ state: newState }) => {
      const newSearchInputText = (newState && newState.searchInputText) || '';
      setSearchInputText(newSearchInputText);
    });
    return unlisten;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history]);

  useEffect(() => {
    if (isDataReady) {
      batch(() => {
        dispatch(SearchResultsSlice.actions.resetSearchResults());
        if (searchInputText.length >= 2) {
          fetchMore();
        }
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInputText, isDataReady]);

  const [scrollableTarget, setScrollableTarget] = useState();
  const scrollParentRef = useRef();

  const onDialogEntered = () => {
    setScrollableTarget(scrollParentRef.current);
  };

  const viewableTabs = tabItems
    .filter((it) => it.condition)
    .map(({ label }) => <StyledTab key={label} label={label} />);

  const viewableTabsList = tabItems
    .filter((it) => it.condition)
    .map(({ type }) => type);

  return (
    <TabsContextStateProvider>
      <StyledDialog
        fullScreen
        open={open}
        onClose={onClose}
        onEntered={onDialogEntered}
        aria-labelledby="search-modal"
      >
        <HeaderContainer>
          <SearchFieldContainer
            searchText={searchInputText}
            onChange={onSeacrhInputChange}
            onCancel={onClose}
          />
          {showTabs && viewableTabs.length > 2 && (
            <SearchTabs viewableTabs={viewableTabs} />
          )}
        </HeaderContainer>
        <BodyContainer ref={scrollParentRef}>
          <SearchResultTabContentContainer
            showResult={showResult}
            fetchMore={fetchMore}
            scrollableTarget={scrollableTarget}
            viewableTabsList={viewableTabsList}
          />
          <FloatingFooter hashState="/search" />
        </BodyContainer>
        <BackToTopButton scrollableTarget={scrollableTarget} />
      </StyledDialog>
    </TabsContextStateProvider>
  );
};

SearchModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
};

SearchModal.defaultProps = {
  open: false,
  onClose: () => {},
};

export default SearchModal;
